import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { gstService } from '../../services/gstService';
import { 
  GSTConfiguration, 
  HSNMaster, 
  Invoice, 
  PaymentRecord, 
  CreditNote,
  SalesRegisterReport,
  GSTSummaryReport,
  OutstandingInvoicesReport,
  ClientLedgerReport
} from '../../types';

interface GSTState {
  // GST Configuration
  gstConfiguration: GSTConfiguration | null;
  
  // HSN Masters
  hsnMasters: HSNMaster[];
  hsnMastersLoading: boolean;
  
  // Invoices
  invoices: Invoice[];
  currentInvoice: Invoice | null;
  invoicesLoading: boolean;
  
  // Payments
  payments: PaymentRecord[];
  paymentsLoading: boolean;
  
  // Credit Notes
  creditNotes: CreditNote[];
  creditNotesLoading: boolean;
  
  // Reports
  reports: {
    salesRegister: SalesRegisterReport | null;
    gstSummary: GSTSummaryReport | null;
    outstandingInvoices: OutstandingInvoicesReport | null;
    clientLedger: ClientLedgerReport | null;
  };
  reportsLoading: boolean;
  
  // Filters
  filters: {
    search?: string;
    status?: string;
    paymentMethod?: string;
    dateRange?: { start: string; end: string };
  };
  
  // General loading and error states
  isLoading: boolean;
  error: string | null;
  
  // Pagination
  pagination: {
    page: number;
    pageSize: number;
    total: number;
  };
}

const initialState: GSTState = {
  gstConfiguration: null,
  hsnMasters: [],
  hsnMastersLoading: false,
  invoices: [],
  currentInvoice: null,
  invoicesLoading: false,
  payments: [],
  paymentsLoading: false,
  creditNotes: [],
  creditNotesLoading: false,
  reports: {
    salesRegister: null,
    gstSummary: null,
    outstandingInvoices: null,
    clientLedger: null,
  },
  reportsLoading: false,
  filters: {},
  isLoading: false,
  error: null,
  pagination: {
    page: 1,
    pageSize: 25,
    total: 0,
  },
};

// GST Configuration Thunks
export const fetchGSTConfiguration = createAsyncThunk(
  'gst/fetchGSTConfiguration',
  async (_, { rejectWithValue }) => {
    try {
      const response = await gstService.getGSTConfiguration();
      return response.data?.[0] || null; // Get first configuration
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to fetch GST configuration');
    }
  }
);

export const updateGSTConfiguration = createAsyncThunk(
  'gst/updateGSTConfiguration',
  async ({ id, data }: { id: number; data: Partial<GSTConfiguration['attributes']> }, { rejectWithValue }) => {
    try {
      const response = await gstService.updateGSTConfiguration(id, data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to update GST configuration');
    }
  }
);

// HSN Master Thunks
export const fetchHSNMasters = createAsyncThunk(
  'gst/fetchHSNMasters',
  async (params: Record<string, any> = {}, { rejectWithValue }) => {
    try {
      const response = await gstService.getHSNMasters(params);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to fetch HSN masters');
    }
  }
);

export const createHSNMaster = createAsyncThunk(
  'gst/createHSNMaster',
  async (data: Partial<HSNMaster['attributes']>, { rejectWithValue }) => {
    try {
      const response = await gstService.createHSNMaster(data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to create HSN master');
    }
  }
);

export const updateHSNMaster = createAsyncThunk(
  'gst/updateHSNMaster',
  async ({ id, data }: { id: number; data: Partial<HSNMaster['attributes']> }, { rejectWithValue }) => {
    try {
      const response = await gstService.updateHSNMaster(id, data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to update HSN master');
    }
  }
);

export const deleteHSNMaster = createAsyncThunk(
  'gst/deleteHSNMaster',
  async (id: number, { rejectWithValue }) => {
    try {
      await gstService.deleteHSNMaster(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to delete HSN master');
    }
  }
);

// Invoice Thunks
export const fetchInvoices = createAsyncThunk(
  'gst/fetchInvoices',
  async (params: Record<string, any> = {}, { rejectWithValue }) => {
    try {
      const response = await gstService.getInvoices(params);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to fetch invoices');
    }
  }
);

export const fetchInvoice = createAsyncThunk(
  'gst/fetchInvoice',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await gstService.getInvoice(id);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to fetch invoice');
    }
  }
);

export const createInvoice = createAsyncThunk(
  'gst/createInvoice',
  async (data: Partial<Invoice['attributes']>, { rejectWithValue }) => {
    try {
      const response = await gstService.createInvoice(data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to create invoice');
    }
  }
);

export const updateInvoice = createAsyncThunk(
  'gst/updateInvoice',
  async ({ id, data }: { id: number; data: Partial<Invoice['attributes']> }, { rejectWithValue }) => {
    try {
      const response = await gstService.updateInvoice(id, data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to update invoice');
    }
  }
);

// Payment Record Thunks
export const fetchPaymentRecords = createAsyncThunk(
  'gst/fetchPaymentRecords',
  async (params: Record<string, any> = {}, { rejectWithValue }) => {
    try {
      const response = await gstService.getPaymentRecords(params);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to fetch payment records');
    }
  }
);

export const createPaymentRecord = createAsyncThunk(
  'gst/createPaymentRecord',
  async (data: Partial<PaymentRecord['attributes']>, { rejectWithValue }) => {
    try {
      const response = await gstService.createPaymentRecord(data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to create payment record');
    }
  }
);

// Credit Note Thunks
export const fetchCreditNotes = createAsyncThunk(
  'gst/fetchCreditNotes',
  async (params: Record<string, any> = {}, { rejectWithValue }) => {
    try {
      const response = await gstService.getCreditNotes(params);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to fetch credit notes');
    }
  }
);

export const createCreditNote = createAsyncThunk(
  'gst/createCreditNote',
  async (data: Partial<CreditNote['attributes']>, { rejectWithValue }) => {
    try {
      const response = await gstService.createCreditNote(data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to create credit note');
    }
  }
);

// Payment Thunks
export const fetchPayments = createAsyncThunk(
  'gst/fetchPayments',
  async (params: Record<string, any> = {}, { rejectWithValue }) => {
    try {
      const response = await gstService.getPaymentRecords(params);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to fetch payments');
    }
  }
);

export const createPayment = createAsyncThunk(
  'gst/createPayment',
  async (data: Partial<PaymentRecord['attributes']>, { rejectWithValue }) => {
    try {
      const response = await gstService.createPaymentRecord(data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to create payment');
    }
  }
);

export const updatePayment = createAsyncThunk(
  'gst/updatePayment',
  async ({ id, data }: { id: number; data: Partial<PaymentRecord['attributes']> }, { rejectWithValue }) => {
    try {
      const response = await gstService.updatePaymentRecord(id, data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to update payment');
    }
  }
);

// Report Generation Thunks
export const generateSalesRegisterReport = createAsyncThunk(
  'gst/generateSalesRegisterReport',
  async ({ startDate, endDate }: { startDate: string; endDate: string }, { rejectWithValue }) => {
    try {
      const response = await gstService.getSalesRegisterReport(startDate, endDate);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to generate sales register report');
    }
  }
);

export const generateGSTSummaryReport = createAsyncThunk(
  'gst/generateGSTSummaryReport',
  async (period: string, { rejectWithValue }) => {
    try {
      const response = await gstService.getGSTSummaryReport(period);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to generate GST summary report');
    }
  }
);

export const generateOutstandingInvoicesReport = createAsyncThunk(
  'gst/generateOutstandingInvoicesReport',
  async (_, { rejectWithValue }) => {
    try {
      const response = await gstService.getOutstandingInvoicesReport();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to generate outstanding invoices report');
    }
  }
);

export const generateClientLedgerReport = createAsyncThunk(
  'gst/generateClientLedgerReport',
  async ({ clientId, startDate, endDate }: { clientId: number; startDate?: string; endDate?: string }, { rejectWithValue }) => {
    try {
      const response = await gstService.getClientLedgerReport(clientId, startDate, endDate);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to generate client ledger report');
    }
  }
);

const gstSlice = createSlice({
  name: 'gst',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentInvoice: (state, action: PayloadAction<Invoice | null>) => {
      state.currentInvoice = action.payload;
    },
    clearCurrentInvoice: (state) => {
      state.currentInvoice = null;
    },
    setPaymentFilters: (state, action: PayloadAction<Partial<GSTState['filters']>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearPaymentFilters: (state) => {
      state.filters = {};
    },
  },
  extraReducers: (builder) => {
    builder
      // GST Configuration
      .addCase(fetchGSTConfiguration.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchGSTConfiguration.fulfilled, (state, action) => {
        state.isLoading = false;
        state.gstConfiguration = action.payload;
      })
      .addCase(fetchGSTConfiguration.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(updateGSTConfiguration.fulfilled, (state, action) => {
        state.gstConfiguration = action.payload;
      })

      // HSN Masters
      .addCase(fetchHSNMasters.pending, (state) => {
        state.hsnMastersLoading = true;
        state.error = null;
      })
      .addCase(fetchHSNMasters.fulfilled, (state, action) => {
        state.hsnMastersLoading = false;
        state.hsnMasters = action.payload.data || [];
        state.pagination = action.payload.meta?.pagination || state.pagination;
      })
      .addCase(fetchHSNMasters.rejected, (state, action) => {
        state.hsnMastersLoading = false;
        state.error = action.payload as string;
      })
      .addCase(createHSNMaster.fulfilled, (state, action) => {
        state.hsnMasters.push(action.payload);
      })
      .addCase(updateHSNMaster.fulfilled, (state, action) => {
        const index = state.hsnMasters.findIndex(hsn => hsn.id === action.payload.id);
        if (index !== -1) {
          state.hsnMasters[index] = action.payload;
        }
      })
      .addCase(deleteHSNMaster.fulfilled, (state, action) => {
        state.hsnMasters = state.hsnMasters.filter(hsn => hsn.id !== action.payload);
      })

      // Invoices
      .addCase(fetchInvoices.pending, (state) => {
        state.invoicesLoading = true;
        state.error = null;
      })
      .addCase(fetchInvoices.fulfilled, (state, action) => {
        state.invoicesLoading = false;
        state.invoices = action.payload.data || [];
        state.pagination = action.payload.meta?.pagination || state.pagination;
      })
      .addCase(fetchInvoices.rejected, (state, action) => {
        state.invoicesLoading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchInvoice.fulfilled, (state, action) => {
        state.currentInvoice = action.payload;
      })
      .addCase(createInvoice.fulfilled, (state, action) => {
        state.invoices.unshift(action.payload);
      })
      .addCase(updateInvoice.fulfilled, (state, action) => {
        const index = state.invoices.findIndex(invoice => invoice.id === action.payload.id);
        if (index !== -1) {
          state.invoices[index] = action.payload;
        }
        if (state.currentInvoice?.id === action.payload.id) {
          state.currentInvoice = action.payload;
        }
      })

      // Payment Records
      .addCase(fetchPaymentRecords.pending, (state) => {
        state.paymentsLoading = true;
        state.error = null;
      })
      .addCase(fetchPaymentRecords.fulfilled, (state, action) => {
        state.paymentsLoading = false;
        state.payments = action.payload.data || [];
      })
      .addCase(fetchPaymentRecords.rejected, (state, action) => {
        state.paymentsLoading = false;
        state.error = action.payload as string;
      })
      .addCase(createPaymentRecord.fulfilled, (state, action) => {
        state.payments.unshift(action.payload);
      })

      // Credit Notes
      .addCase(fetchCreditNotes.pending, (state) => {
        state.creditNotesLoading = true;
        state.error = null;
      })
      .addCase(fetchCreditNotes.fulfilled, (state, action) => {
        state.creditNotesLoading = false;
        state.creditNotes = action.payload.data || [];
      })
      .addCase(fetchCreditNotes.rejected, (state, action) => {
        state.creditNotesLoading = false;
        state.error = action.payload as string;
      })
      .addCase(createCreditNote.fulfilled, (state, action) => {
        state.creditNotes.unshift(action.payload);
      })

      // Payments
      .addCase(fetchPayments.pending, (state) => {
        state.paymentsLoading = true;
        state.error = null;
      })
      .addCase(fetchPayments.fulfilled, (state, action) => {
        state.paymentsLoading = false;
        state.payments = action.payload.data || [];
        state.pagination = action.payload.meta?.pagination || state.pagination;
      })
      .addCase(fetchPayments.rejected, (state, action) => {
        state.paymentsLoading = false;
        state.error = action.payload as string;
      })
      .addCase(createPayment.fulfilled, (state, action) => {
        state.payments.unshift(action.payload);
      })
      .addCase(updatePayment.fulfilled, (state, action) => {
        const index = state.payments.findIndex(payment => payment.id === action.payload.id);
        if (index !== -1) {
          state.payments[index] = action.payload;
        }
      })

      // Report Generation
      .addCase(generateSalesRegisterReport.pending, (state) => {
        state.reportsLoading = true;
        state.error = null;
      })
      .addCase(generateSalesRegisterReport.fulfilled, (state, action) => {
        state.reportsLoading = false;
        state.reports.salesRegister = action.payload;
      })
      .addCase(generateSalesRegisterReport.rejected, (state, action) => {
        state.reportsLoading = false;
        state.error = action.payload as string;
      })
      .addCase(generateGSTSummaryReport.pending, (state) => {
        state.reportsLoading = true;
        state.error = null;
      })
      .addCase(generateGSTSummaryReport.fulfilled, (state, action) => {
        state.reportsLoading = false;
        state.reports.gstSummary = action.payload;
      })
      .addCase(generateGSTSummaryReport.rejected, (state, action) => {
        state.reportsLoading = false;
        state.error = action.payload as string;
      })
      .addCase(generateOutstandingInvoicesReport.pending, (state) => {
        state.reportsLoading = true;
        state.error = null;
      })
      .addCase(generateOutstandingInvoicesReport.fulfilled, (state, action) => {
        state.reportsLoading = false;
        state.reports.outstandingInvoices = action.payload;
      })
      .addCase(generateOutstandingInvoicesReport.rejected, (state, action) => {
        state.reportsLoading = false;
        state.error = action.payload as string;
      })
      .addCase(generateClientLedgerReport.pending, (state) => {
        state.reportsLoading = true;
        state.error = null;
      })
      .addCase(generateClientLedgerReport.fulfilled, (state, action) => {
        state.reportsLoading = false;
        state.reports.clientLedger = action.payload;
      })
      .addCase(generateClientLedgerReport.rejected, (state, action) => {
        state.reportsLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { 
  clearError, 
  setCurrentInvoice, 
  clearCurrentInvoice, 
  setPaymentFilters, 
  clearPaymentFilters 
} = gstSlice.actions;
export default gstSlice.reducer; 